import {
  cloneInstrument,
  cloneWavetablePreset,
  type Instrument,
  type InstrumentSlot,
  type WavetablePreset,
  wavetableInstrument,
} from "../../../../src/lib/audio";
import {
  isWavetablePreset,
  newWavetablePresetId,
  sanitizeWavetableBank,
  sanitizeWtPresetCategory,
  sanitizeWtPresetTags,
  wtCategoryLabel,
  type NamedWavetablePreset,
  type WtPresetCategoryId,
} from "./wavetableBank";

export type WavetableBankState = {
  instruments: Instrument[];
  bank: NamedWavetablePreset[];
  slotBankIds: Array<string | undefined>;
};

export type WavetableBankResult = WavetableBankState & {
  status: string;
  changedSlots: InstrumentSlot[];
};

function cloneState(state: WavetableBankState): WavetableBankState {
  return {
    instruments: state.instruments.map(cloneInstrument),
    bank: sanitizeWavetableBank(state.bank),
    slotBankIds: [...state.slotBankIds],
  };
}

function slotPreset(state: WavetableBankState, slot: InstrumentSlot): WavetablePreset | null {
  const inst = state.instruments[slot];
  return inst?.kind === "wavetable" ? inst.preset : null;
}

export function initialWtSlotBankIds(slotCount: number): Array<string | undefined> {
  return Array.from({ length: slotCount }, () => undefined);
}

export function syncWtSlotBankIdsFromInstruments(state: WavetableBankState): WavetableBankState {
  const next = cloneState(state);
  for (let i = 0; i < next.instruments.length; i++) {
    const inst = next.instruments[i];
    if (inst?.kind !== "wavetable") {
      continue;
    }
    const match = next.bank.find(entry => entry.name === inst.preset.name);
    if (match) {
      next.slotBankIds[i] = match.id;
    }
  }
  return next;
}

export function applyWtBankEntry(
  state: WavetableBankState,
  slot: InstrumentSlot,
  bankId: string,
): WavetableBankResult | null {
  const entry = state.bank.find(item => item.id === bankId);
  if (!entry) {
    return null;
  }
  const next = cloneState(state);
  next.instruments[slot] = wavetableInstrument({ ...entry.preset, name: entry.name });
  next.slotBankIds[slot] = entry.id;
  return {
    ...next,
    status: `Loaded “${entry.name}” into slot ${slot + 1}`,
    changedSlots: [slot],
  };
}

/** Returns null when there is no bank entry to overwrite — caller should run Save as. */
export function saveWtBankCurrent(
  state: WavetableBankState,
  slot: InstrumentSlot,
  selectedBankId?: string,
): WavetableBankResult | null {
  const preset = slotPreset(state, slot);
  if (!preset) {
    return null;
  }
  const id = state.slotBankIds[slot] ?? selectedBankId;
  const entryIndex = id ? state.bank.findIndex(item => item.id === id) : -1;
  if (entryIndex < 0) {
    return null;
  }
  const next = cloneState(state);
  const entry = next.bank[entryIndex]!;
  entry.preset = cloneWavetablePreset({ ...preset, name: entry.name });
  next.instruments[slot] = wavetableInstrument({ ...preset, name: entry.name });
  next.slotBankIds[slot] = entry.id;
  return {
    ...next,
    status: `Saved “${entry.name}”`,
    changedSlots: [slot],
  };
}

export function saveWtBankAs(
  state: WavetableBankState,
  slot: InstrumentSlot,
  name: string,
  overwrite: boolean,
): WavetableBankResult | { conflict: true } | null {
  const preset = slotPreset(state, slot);
  if (!preset) {
    return null;
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  const next = cloneState(state);
  const existing = next.bank.find(entry => entry.name === trimmed);
  if (existing && !overwrite) {
    return { conflict: true };
  }
  if (existing) {
    existing.preset = cloneWavetablePreset({ ...preset, name: trimmed });
    next.slotBankIds[slot] = existing.id;
    next.instruments[slot] = wavetableInstrument({ ...preset, name: trimmed });
  } else {
    const id = newWavetablePresetId();
    next.bank.push({
      id,
      name: trimmed,
      builtin: false,
      category: "user",
      preset: cloneWavetablePreset({ ...preset, name: trimmed }),
    });
    next.slotBankIds[slot] = id;
    next.instruments[slot] = wavetableInstrument({ ...preset, name: trimmed });
  }
  return {
    ...next,
    status: `Saved “${trimmed}”`,
    changedSlots: [slot],
  };
}

export function renameWtBankEntry(
  state: WavetableBankState,
  bankId: string,
  name: string,
): WavetableBankResult | { statusOnly: string } | null {
  const trimmed = name.trim();
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const entry = state.bank[entryIndex]!;
  if (!trimmed || trimmed === entry.name) {
    return null;
  }
  if (state.bank.some(item => item.id !== entry.id && item.name === trimmed)) {
    return { statusOnly: `Name “${trimmed}” is already used` };
  }
  const next = cloneState(state);
  const target = next.bank[entryIndex]!;
  target.name = trimmed;
  target.preset = cloneWavetablePreset({ ...target.preset, name: trimmed });
  const changedSlots: InstrumentSlot[] = [];
  for (let i = 0; i < next.instruments.length; i++) {
    if (next.slotBankIds[i] === bankId && next.instruments[i]?.kind === "wavetable") {
      const current = next.instruments[i]!;
      next.instruments[i] = wavetableInstrument({ ...current.preset, name: trimmed });
      changedSlots.push(i as InstrumentSlot);
    }
  }
  return {
    ...next,
    status: `Renamed to “${trimmed}”`,
    changedSlots,
  };
}

export function deleteWtBankEntry(
  state: WavetableBankState,
  bankId: string,
): WavetableBankResult | { statusOnly: string } | null {
  const entry = state.bank.find(item => item.id === bankId);
  if (!entry) {
    return null;
  }
  if (entry.builtin) {
    return { statusOnly: "Factory presets cannot be deleted" };
  }
  const next = cloneState(state);
  next.bank = next.bank.filter(item => item.id !== bankId);
  for (let i = 0; i < next.slotBankIds.length; i++) {
    if (next.slotBankIds[i] === bankId) {
      next.slotBankIds[i] = next.bank[0]?.id;
    }
  }
  return {
    ...next,
    status: `Deleted “${entry.name}”`,
    changedSlots: [],
  };
}

export function setWtBankCategory(
  state: WavetableBankState,
  bankId: string,
  category: WtPresetCategoryId,
): WavetableBankResult | null {
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const entry = state.bank[entryIndex]!;
  if (entry.builtin) {
    return null;
  }
  const nextCat = sanitizeWtPresetCategory(category, entry.category);
  if (entry.category === nextCat) {
    return null;
  }
  const next = cloneState(state);
  next.bank[entryIndex]!.category = nextCat;
  return {
    ...next,
    status: `Moved “${entry.name}” to ${wtCategoryLabel(nextCat)}`,
    changedSlots: [],
  };
}

export function setWtBankTags(
  state: WavetableBankState,
  bankId: string,
  tagsRaw: string[],
): WavetableBankResult | null {
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const next = cloneState(state);
  const entry = next.bank[entryIndex]!;
  const tags = sanitizeWtPresetTags(tagsRaw);
  if (tags) {
    entry.tags = tags;
  } else {
    delete entry.tags;
  }
  return {
    ...next,
    status: tags ? `Tagged “${entry.name}” · ${tags.join(", ")}` : `Cleared tags on “${entry.name}”`,
    changedSlots: [],
  };
}

export function buildWtExportEntryPayload(
  state: WavetableBankState,
  bankId: string | undefined,
  slot: InstrumentSlot,
): { json: string; filename: string; status: string } | null {
  const entry = bankId ? state.bank.find(item => item.id === bankId) : undefined;
  const live = slotPreset(state, slot);
  if (!entry && !live) {
    return null;
  }
  const payload = entry
    ? { type: "adam-wavetable-preset", version: 1, preset: entry }
    : {
      type: "adam-wavetable-preset",
      version: 1,
      preset: {
        id: newWavetablePresetId(),
        name: live!.name,
        category: "user" as const,
        preset: live!,
      },
    };
  const baseName = (entry?.name ?? live!.name) || "preset";
  const filename = `${baseName.replace(/\s+/g, "-")}.json`;
  return {
    json: JSON.stringify(payload, null, 2),
    filename,
    status: `Exported ${filename}`,
  };
}

export function buildWtExportAllPayload(state: WavetableBankState): {
  json: string;
  filename: string;
  status: string;
} {
  const bank = sanitizeWavetableBank(state.bank);
  return {
    json: JSON.stringify({ type: "adam-wavetable-bank", version: 1, bank }, null, 2),
    filename: "adam-wavetable-bank.json",
    status: `Exported ${bank.length} wavetable presets`,
  };
}

export function importWtBankData(
  state: WavetableBankState,
  slot: InstrumentSlot,
  data: unknown,
): WavetableBankResult {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid JSON");
  }
  const obj = data as Record<string, unknown>;

  if (
    (obj.type === "adam-wavetable-bank" || obj.type === "curva-wavetable-bank")
    && Array.isArray(obj.bank)
  ) {
    const next = cloneState(state);
    next.bank = sanitizeWavetableBank(obj.bank);
    const synced = syncWtSlotBankIdsFromInstruments(next);
    return {
      ...synced,
      status: `Imported wavetable bank (${synced.bank.length} presets)`,
      changedSlots: [],
    };
  }

  if (
    (obj.type === "adam-wavetable-preset" || obj.type === "curva-wavetable-preset")
    && obj.preset
    && typeof obj.preset === "object"
  ) {
    const wrapped = obj.preset as Partial<NamedWavetablePreset>;
    if (wrapped.preset && isWavetablePreset(wrapped.preset)) {
      return addImportedNamed(state, slot, {
        id: typeof wrapped.id === "string" ? wrapped.id : newWavetablePresetId(),
        name: typeof wrapped.name === "string" ? wrapped.name : wrapped.preset.name || "Imported",
        builtin: false,
        category: sanitizeWtPresetCategory(wrapped.category, "user"),
        ...(sanitizeWtPresetTags(wrapped.tags) ? { tags: sanitizeWtPresetTags(wrapped.tags) } : {}),
        preset: cloneWavetablePreset(wrapped.preset),
      });
    }
  }

  if (isWavetablePreset(obj)) {
    const next = cloneState(state);
    next.instruments[slot] = wavetableInstrument(obj);
    return {
      ...next,
      status: `Loaded preset into slot ${slot + 1} (Save as… to add to bank)`,
      changedSlots: [slot],
    };
  }

  if (Array.isArray(obj)) {
    const next = cloneState(state);
    next.bank = sanitizeWavetableBank([...next.bank, ...obj]);
    return {
      ...next,
      status: `Merged import into bank (${next.bank.length} presets)`,
      changedSlots: [],
    };
  }

  throw new Error("Unrecognized wavetable preset JSON");
}

function addImportedNamed(
  state: WavetableBankState,
  slot: InstrumentSlot,
  entry: NamedWavetablePreset,
): WavetableBankResult {
  const next = cloneState(state);
  const existingIdx = next.bank.findIndex(
    item => item.id === entry.id || item.name === entry.name,
  );
  const tags = sanitizeWtPresetTags(entry.tags);
  let added: NamedWavetablePreset;
  if (existingIdx >= 0 && !next.bank[existingIdx]!.builtin) {
    added = {
      id: next.bank[existingIdx]!.id,
      name: entry.name,
      builtin: false,
      category: sanitizeWtPresetCategory(entry.category, next.bank[existingIdx]!.category),
      ...(tags ? { tags } : {}),
      preset: cloneWavetablePreset({ ...entry.preset, name: entry.name }),
    };
    next.bank[existingIdx] = added;
  } else {
    added = {
      id: newWavetablePresetId(),
      name: entry.name,
      builtin: false,
      category: sanitizeWtPresetCategory(entry.category, "user"),
      ...(tags ? { tags } : {}),
      preset: cloneWavetablePreset({ ...entry.preset, name: entry.name }),
    };
    next.bank.push(added);
  }
  next.slotBankIds[slot] = added.id;
  next.instruments[slot] = wavetableInstrument({ ...added.preset, name: added.name });
  return {
    ...next,
    status: `Imported “${added.name}”`,
    changedSlots: [slot],
  };
}
